import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FilteredItem } from '@core/models/filtered-item.model';
import { Observable, Subject } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-tv-select-items-dialog',
  templateUrl: './tv-select-items-dialog.component.html',
  styleUrls: ['./tv-select-items-dialog.component.scss'],
})
export class TvSelectItemsDialogComponent implements OnInit {
  public panelOpened = false;
  private readonly renderedItemLimit = 100;

  public searchFormControl = new FormControl('');

  public selectAllOptions = false;
  public filteredOptionList$: Observable<any[]>;

  public isLoadingItemList: boolean = false;
  public filteredItemList$: Observable<any[]>;

  public selectedOptions: Set<any> = new Set();

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    public dialogRef: MatDialogRef<TvSelectItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { itemList: Observable<any[]>; itemListAlreadySelected?: any[] | Set<any> }
  ) {}

  ngOnInit(): void {
    this.initSelectedOptions();
    this.initFilteredItemList();
    this.initFilteredOptions();
  }

  private initSelectedOptions() {
    const selectedItemIds = this.getSelectedItemIds();
    if (selectedItemIds.size === 0) return;

    this.data.itemList.pipe(take(1)).subscribe(items => {
      items.forEach(item => {
        if (selectedItemIds.has(this.getItemId(item))) {
          this.selectedOptions.add(item);
        }
      });
    });
  }

  private initFilteredOptions() {
    this.filteredOptionList$ = this.data.itemList;
  }

  initFilteredItemList() {
    // Filtrage des éléments en fonction de la recherche
    this.filteredItemList$ = this.searchFormControl.valueChanges.pipe(
      startWith(''), // Démarrer avec une chaîne vide
      switchMap(searchTerm => this.filterList(searchTerm || ''))
    );
  }

  // Méthode qui retourne un Observable filtré
  filterList(searchTerm: string = ''): Observable<any[]> {
    const lowerCaseTerm = searchTerm.toLowerCase();

    // Filtrer la liste en fonction du terme de recherche
    return this.data.itemList.pipe(
      map(items =>
        items
          .filter(item => item.id?.toString().includes(lowerCaseTerm) || item.name.toLowerCase().includes(lowerCaseTerm))
          .slice(0, this.renderedItemLimit)
      )
    );
  }

  onCancelSelectClick() {
    this.selectedOptions.clear();
    this.dialogRef.close();
  }

  onSaveChosenItemList() {
    this.dialogRef.close(this.selectedOptions);
  }

  onSelectAllChange(event: any) {
    this.selectAllOptions = event.checked;
    this.filteredOptionList$?.pipe(take(1)).subscribe(options => {
      options.forEach(item => {
        if (this.selectAllOptions) {
          this.selectedOptions.add(item);
        } else if (!this.selectAllOptions) {
          this.deleteSelectedOption(item);
        }
      });
    });
  }

  isOptionSelected(item) {
    return Array.from(this.selectedOptions).some(selectedItem => this.getItemId(selectedItem) === this.getItemId(item));
  }

  onOptionChange(event: any, item: any) {
    if (event.checked) {
      if (!this.isOptionSelected(item)) {
        this.selectedOptions.add(item);
      }
    } else {
      this.deleteSelectedOption(item);
    }
  }

  public displayItem(item: FilteredItem): string {
    return item?.name && item?.id ? `${item.name} (${item.id})` : '';
  }

  public trackByItemId = (index: number, item: any): any => this.getItemId(item);

  private deleteSelectedOption(item: any) {
    const itemId = this.getItemId(item);
    Array.from(this.selectedOptions).forEach(selectedItem => {
      if (this.getItemId(selectedItem) === itemId) {
        this.selectedOptions.delete(selectedItem);
      }
    });
  }

  private getSelectedItemIds(): Set<any> {
    const selectedItems = this.data.itemListAlreadySelected instanceof Set
      ? Array.from(this.data.itemListAlreadySelected)
      : this.data.itemListAlreadySelected || [];

    return new Set(selectedItems.map(item => this.getItemId(item)).filter(itemId => itemId !== undefined && itemId !== null));
  }

  private getItemId(item: any) {
    return item?.id ?? item?.item?.id ?? item?.categoryValue?.id ?? item?.category?.id;
  }
}
